import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { getCanonicalUrl } from '../utils/canonicalUrl';
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Link as MuiLink,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PolicyIcon from '@mui/icons-material/Policy';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Link } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import WhyChooseUs from '../components/WhyChooseUs';
import Testimonials from '../components/Testimonials';
import OurProcess from '../components/OurProcess';
import GatewayHighlight from '../components/GatewayHighlight';
import FeatureIcons from '../components/FeatureIcons';
import CaseStudy from '../components/CaseStudy';
import ContactForm from '../components/ContactForm';
import posts from '../content/resourcesPosts';

const caseStudyPosts = posts.filter((p) => p.id === 3 || p.id === 4);
const caseStudyImages = [
  '/assets/visuals/re-profile-blue-ai-hud.webp',
  '/assets/visuals/re-profile-orange-ai-hud.webp',
];

export default function Home() {
  const location = useLocation();
  const canonicalUrl = getCanonicalUrl(location.pathname);
  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <title>Robinson Engineering - Pragmatic AI Solutions</title>
        <meta name="description" content="Home page highlighting our AI consulting services, case studies and free assessment offer." />
        <meta property="og:title" content="Robinson Engineering - Pragmatic AI Solutions" />
        <meta property="og:description" content="Discover our pragmatic AI services and request your free assessment." />
        <meta property="og:image" content="/assets/visuals/re-profile-blue-ai-hud.webp" />
      </Helmet>
      <Box
        data-testid="hero-section"
        sx={{
          position: 'relative',
          backgroundImage:
            'linear-gradient(rgba(10,16,30,0.8), rgba(24,45,70,0.8)), url("/assets/hero/re-profile-silhouette-light.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'common.white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
        }}
      >
        {/* Fallback image for older browsers */}
        <Box
          component="img"
          src="/assets/hero/re-profile-silhouette-light.webp"
          alt="Robinson Engineering hero silhouette"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0,
            zIndex: -1,
          }}
        />
        <Container>
          <Typography variant="h2" component="h1" gutterBottom>
            Empowering Your Toughest Engineering Challenges
          </Typography>
          <Typography
            variant="h6"
            component="p"
            gutterBottom
            sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
          >
            We help organisations solve their most challenging engineering problems using pragmatic AI solutions.
          </Typography>
          <Typography
            variant="body1"
            component="p"
            gutterBottom
            sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
          >
            We work fast, iterate faster, and ensure everything we build delivers real ROI in a people-first culture.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to="/services/free-ai-assessment"
            size="large"
          >
            FREE AI ASSESSMENT
          </Button>
        </Container>
      </Box>

      <GatewayHighlight />

      <FeatureIcons />

      <Container sx={{ py: 8, color: 'common.white', backgroundColor: 'primary.main' }}>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          color="common.white"
        >
          SERVICES
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ maxWidth: 700, mx: 'auto', color: 'common.white', mb: 4 }}
        >
          Helping you build the foundations for AI success, establish robust governance,
          convert priority workflows into prototypes, and transform challenges into scalable solutions.
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <ServiceCard
              icon={<SearchIcon />}
              title="AI Readiness Assessment"
              description="Build the foundations for AI success with a tailored roadmap covering data, technology, people and governance."
              link="/services/ai-readiness-assessment"
              background="/assets/visuals/re-code_image.jpg"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ServiceCard
              icon={<PolicyIcon />}
              title="AI Policy Development"
              description="Establish robust, ethical and compliant AI governance frameworks tailored to your organisation."
              link="/services/ai-policy-development"
              background="/assets/visuals/re-infosec.jpg"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ServiceCard
              icon={<RocketLaunchIcon />}
              title="End-to-End AI Consulting"
              description="Transform your toughest challenges into scalable AI solutions, from strategy through production."
              link="/services/end-to-end-ai-consulting"
              background="/assets/visuals/re-server_rack_girl.jpg"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ServiceCard
              icon={<AutoFixHighIcon />}
              title="Rapid AI Automation Sprint"
              description="Turn one high-friction workflow into a working AI-assisted prototype with clear controls and an ROI path."
              link="/services/rapid-ai-automation-sprint"
              background="/assets/visuals/re-back-matrix-coder.webp"
            />
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" gutterBottom>
            Curious about real-world impact?
          </Typography>
          <MuiLink component={Link} to="/resources" color="secondary.main">
            Explore our latest case studies
          </MuiLink>
          <Typography variant="body2">
            including successes in law firms and oil &amp; gas.
          </Typography>
        </Box>
      </Container>
      {caseStudyPosts.map((post, idx) => (
        <Container key={post.id} sx={{ backgroundColor: 'background.paper' }}>
          <CaseStudy
            title={post.title}
            body={post.content}
            image={caseStudyImages[idx]}
            imageAlt={`${post.title} visual`}
            reverse={idx % 2 === 1}
          />
        </Container>
      ))}
      <WhyChooseUs />
      <Testimonials />
      <OurProcess />
      <Box sx={{ backgroundColor: '#182D46', color: 'common.white', py: 8 }}>
        <Container maxWidth="sm">
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Contact us any time
          </Typography>
          <ContactForm />
        </Container>
      </Box>
    </>
  );
}
